/* eslint-disable react/no-danger */

import React from 'react';
import { useTranslation } from 'next-i18next';

import {
  Breadcrumbs, Error, ResponsiveImage, Skeleton
} from 'components/ui';
import { useMediaQuery } from 'hooks/useMediaQuery';

import ProductsListing from './ProductsListing';

import type { ProductCard } from 'components/product';
import type {
  IAnalyticsListProps, IBreadcrumb, IPageUrlParams, IProductsListing
} from 'types';

import styles from './ProductsListingTemplate.module.scss';

interface Props {
  isLoading: boolean,
  isError: boolean,
  //
  urlParams: IPageUrlParams,
  breadcrumbs: IBreadcrumb[],
  //
  title: string,
  banner?: any,
  navigation?: any,
  //
  data?: IProductsListing,
  //
  productListProps?: IAnalyticsListProps,
  productCardProps?: Partial<React.ComponentProps<typeof ProductCard>>,
  promotion?: {code: string, name: string},
  //
  scrollToProducts?: boolean
}

const ProductsListingTemplate = (props: Props) => {
  const {
    isLoading,
    isError,
    //
    urlParams,
    breadcrumbs,
    //
    title,
    banner,
    navigation,
    //
    data,
    //
    productListProps = {},
    productCardProps = {},
    promotion,
    //
    scrollToProducts
  } = props;

  // Hooks

  const { t } = useTranslation();

  // Add the current page to the breadcrumbs

  const pageBreadcrumbs: IBreadcrumb[] = [
    ...breadcrumbs,
    { name: data?.name || title || '', translate: false, }
  ];

  // Loading state

  if (isLoading) {
    <div>
      <Breadcrumbs className={styles.breadcrumbs} items={pageBreadcrumbs} />
      <h1 className={styles.title}>{t(title)}</h1>
      <div className={styles.skeleton}>
        <div><Skeleton /></div>
        <div><Skeleton /></div>
      </div>
    </div>;
  }

  // Error state

  if (isError) {
    return (
      <div>
        <Breadcrumbs className={styles.breadcrumbs} items={pageBreadcrumbs} />
        <h1 className={styles.title}>{t(title)}</h1>
        <Error />
      </div>
    );
  }

  if (data) {

    const {
      slug: code,
      name,
      items: products,
      categories,
      pages: totalPages,
      page: activePage,
      total: count,
      filters,
      orderBy,
    } = data;

    return (
      <div>
        {/* Breadcrumbs */}
        <Breadcrumbs
          className={styles.breadcrumbs}
          items={pageBreadcrumbs}
        />

        {/* Title / Banner */}
        <ProductsListingBanner
          title={name}
          banner={banner}
        />

        {/* Products Listing */}
        <ProductsListing
          className={styles.listing}
          emptyClassName={styles.empty}
          categoryCode={code}
          title={name}
          navigation={navigation}
          urlParams={urlParams}
          categories={categories}
          count={count}
          totalPages={totalPages}
          activePage={activePage}
          products={products}
          productListProps={productListProps}
          productCardProps={productCardProps}
          promotion={promotion}
          filters={filters}
          orderBy={orderBy}
          scrollToProducts={scrollToProducts}
        />
      </div>
    );
  }

  return null;
};

// Banner

const ProductsListingBanner = (props: Pick<Props, 'title' | 'banner'>) => {
  const { title, banner = {} } = props;

  const {
    desktop: desktopBanner, responsive: responsiveBanner, alt, description
  } = banner;

  const { t } = useTranslation();
  const isBreakpoint = useMediaQuery(`(max-width: ${styles.breakpoint})`);

  const hasBanner = (isBreakpoint && responsiveBanner?.default) || (!isBreakpoint && desktopBanner?.default);

  if (!hasBanner) {
    return (
      <>
        <h1 className={styles.title}>
          {t(title)}
        </h1>
        {
          description && (
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )
        }
      </>
    );
  }

  return (
    <div className={styles.banner}>
      <ResponsiveImage
        optimize
        imageClassName={styles.image}
        alt={alt}
        desktopProps={{
          src: desktopBanner,
          width: 1400,
          height: 391,
          priority: true,
          alt: t(alt)
        }}
        responsiveProps={{
          src: responsiveBanner?.default,
          width: 700,
          height: 395,
          alt: t(alt)
        }}
      />
      {
        description && (
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )
      }
    </div>
  );
};

export default ProductsListingTemplate;
